import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { formatTimestamp } from "./formatters";
import { LogEntry, LogMetadata } from "./types";
import { tokens, EMPTY } from "./tokens";

interface RowProps {
  label: string;
  value: unknown;
  mono?: boolean;
}

function Row({ label, value, mono }: RowProps) {
  if (!value && value !== 0) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "110px 1fr",
        gap: 1,
        mb: 0.75,
        alignItems: "baseline",
      }}
    >
      <Typography
        variant="caption"
        sx={{ color: tokens.textSecondary, fontSize: "0.68rem", justifySelf: "end", textAlign: "right" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          wordBreak: "break-all",
          whiteSpace: "pre-wrap",
          fontFamily: mono ? tokens.mono : "inherit",
          fontSize: "0.78rem",
          color: tokens.textPrimary,
        }}
      >
        {typeof value === "object" ? JSON.stringify(value, null, 2) : String(value)}
      </Typography>
    </Box>
  );
}

// The full log-detail body (metadata rows, message, stack trace, tags, raw JSON).
// Shared by the right-side drawer (Log Monitor) and the modal accordion rows
// (Error Monitor) so both show identical content.
export default function LogDetailContent({ log }: { log: LogEntry }) {
  const meta: LogMetadata = log.metadata || {};

  return (
    <Box>
      <Row label="Timestamp" value={formatTimestamp(log.timestamp)} />
      <Row label="Request ID" value={meta.requestId !== "unknown" ? meta.requestId : null} mono />
      <Row label="Correlation ID" value={meta.correlationId} mono />
      <Row label="Status Code" value={meta.statusCode} />
      <Row label="Error Code" value={meta.errorCode} mono />
      <Row label="Origin API" value={meta.originApi} />
      <Row label="Endpoint" value={meta.url || meta.endpoint} mono />
      <Row label="HTTP Method" value={meta.httpMethod} />
      <Row label="Response Time" value={meta.responseTime ? `${meta.responseTime}ms` : null} />
      <Row label="Source File" value={meta.sourceFile} mono />
      <Row label="Lambda" value={meta.lambda} mono />
      <Row label="Service" value={meta.jsonService} />
      <Row label="Log Source" value={log.logSource} />
      <Row label="PNR" value={meta.pnr} mono />

      <Divider sx={{ my: 1.5, borderColor: tokens.divider }} />

      {/* Message */}
      <Typography variant="caption" sx={{ color: tokens.textSecondary, mb: 0.5, display: "block" }}>
        Message
      </Typography>
      <Box
        sx={{
          p: 1.5,
          bgcolor: tokens.elevated,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 1,
          mb: 2,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-word",
            lineHeight: 1.6,
            fontSize: "0.8rem",
            color: tokens.textPrimary,
          }}
        >
          {log.message || EMPTY}
        </Typography>
      </Box>

      {/* Stack trace — dark code block kept by convention */}
      {meta.stack && (
        <>
          <Typography variant="caption" sx={{ color: tokens.textSecondary, mb: 0.5, display: "block" }}>
            Stack Trace
          </Typography>
          <Box
            component="pre"
            sx={{
              bgcolor: "#202124",
              color: "#e8eaed",
              p: 1.5,
              borderRadius: 1,
              fontSize: "0.7rem",
              fontFamily: tokens.mono,
              overflow: "auto",
              maxHeight: 220,
              whiteSpace: "pre-wrap",
              wordBreak: "break-all",
              lineHeight: 1.55,
              mb: 2,
            }}
          >
            {String(meta.stack)}
          </Box>
        </>
      )}

      {/* Tags */}
      {log.filteredTags?.length > 0 && (
        <Box mb={2}>
          <Typography variant="caption" sx={{ color: tokens.textSecondary, mb: 0.5, display: "block" }}>
            Tags
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {log.filteredTags.map((t, i) => (
              <Chip
                key={i}
                label={t}
                size="small"
                variant="outlined"
                sx={{ borderColor: tokens.divider, color: tokens.textSecondary }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Raw metadata */}
      <Typography variant="caption" sx={{ color: tokens.textSecondary, mb: 0.5, display: "block" }}>
        Raw Metadata
      </Typography>
      <Box
        component="pre"
        sx={{
          bgcolor: tokens.elevated,
          border: `1px solid ${tokens.divider}`,
          borderRadius: 1,
          p: 1.5,
          fontSize: "0.72rem",
          lineHeight: 1.5,
          fontFamily: tokens.mono,
          overflow: "auto",
          minHeight: 200,
          maxHeight: 460,
          color: tokens.textPrimary,
        }}
      >
        {JSON.stringify(meta, null, 2)}
      </Box>
    </Box>
  );
}
